/**
 * Helpers for deriving invite-code metadata from the request URL.
 *
 * - The trailing path segment (`/invite/<source>`) becomes the marketing
 *   `source` — path-based so ad-blockers don't strip it.
 * - UTM query params become the free-text `internal_note`.
 */

type SearchParams = Record<string, string | string[] | undefined>

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

/** Build a readable note from any utm_* params present: "utm_source=x; utm_campaign=y". */
export function buildUtmNote(searchParams: SearchParams | undefined): string | null {
  if (!searchParams) return null
  const parts: string[] = []
  for (const key of UTM_KEYS) {
    const raw = searchParams[key]
    const value = (Array.isArray(raw) ? raw[0] : raw)?.trim()
    if (value) parts.push(`${key}=${value}`)
  }
  return parts.length ? parts.join('; ').slice(0, 2000) : null
}

/** Same as buildUtmNote but from a raw query string (e.g. window.location.search). */
export function buildUtmNoteFromQuery(search: string | null | undefined): string | null {
  if (!search) return null
  const params = new URLSearchParams(search)
  const record: Record<string, string> = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) record[key] = value
  }
  return buildUtmNote(record)
}

/** Normalize a path-segment source to a safe slug; null if empty/invalid. */
export function sanitizeSource(raw: string | null | undefined): string | null {
  if (!raw) return null
  const slug = raw.trim().replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 120)
  return slug || null
}
